import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { TABLE_SORTER_TOOLTIP } from '../../../shared/ui/list-styles.js';
// UI-only drawer with platform user details: memberships and live sessions.
// Data comes from the page (useAdminUserDetail); no fetching here.
import type { ReactElement } from 'react';
import { Badge, Descriptions, Drawer, Empty, Skeleton, Table, Tag, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { useTranslation } from '@govard/i18n-core/react';

import { formatDateTime } from '../lib/format.js';
import type { AdminUserDetail, AdminUserSession } from '../api/admin.client.js';

const { Text } = Typography;

export interface AdminUserDrawerProps {
  open: boolean;
  userId: string | null;
  detail: AdminUserDetail | undefined;
  isLoading: boolean;
  onClose: () => void;
}

const sessionColumns = (t: (key: string) => string): Array<ColumnType<AdminUserSession>> => [
  {
    title: t('crm.admin.users.session_device'),
    dataIndex: 'userAgent',
    key: 'userAgent',
    ellipsis: true,
    render: (value: string | null) => (
      <Text style={{ fontSize: 12 }}>{value ?? t('crm.admin.users.session_unknown_device')}</Text>
    ),
  },
  {
    title: t('crm.admin.users.session_ip'),
    dataIndex: 'ip',
    key: 'ip',
    width: 130,
    render: (value: string | null) => <Text style={{ fontSize: 12 }}>{value ?? '—'}</Text>,
  },
  {
    title: t('crm.admin.users.session_last_used'),
    dataIndex: 'lastUsedAt',
    key: 'lastUsedAt',
    width: 160,
    render: (value: string | null) => <Text style={{ fontSize: 12 }}>{formatDateTime(value)}</Text>,
  },
  {
    title: t('crm.admin.users.session_expires'),
    dataIndex: 'expiresAt',
    key: 'expiresAt',
    width: 160,
    render: (value: string) => <Text style={{ fontSize: 12 }}>{formatDateTime(value)}</Text>,
  },
];

export function AdminUserDrawer({
  open,
  userId,
  detail,
  isLoading,
  onClose,
}: AdminUserDrawerProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={detail ? detail.user.email : t('crm.admin.users.detail_title')}
      destroyOnHidden
    >
      {isLoading || (detail === undefined && userId !== null) ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : detail === undefined ? (
        <Empty description={t('crm.admin.users.detail_empty')} />
      ) : (
        <div className="flex flex-col gap-6">
          <Descriptions
            size="small"
            column={1}
            bordered
            items={[
              { key: 'name', label: t('crm.admin.users.col_name'), children: detail.user.name },
              { key: 'email', label: t('crm.admin.users.col_email'), children: detail.user.email },
              {
                key: 'created',
                label: t('crm.admin.users.col_created'),
                children: formatDateTime(detail.user.createdAt),
              },
              {
                key: 'verified',
                label: t('crm.admin.users.col_verified'),
                children: (
                  <Badge
                    status={detail.user.emailVerified ? 'success' : 'default'}
                    text={
                      detail.user.emailVerified
                        ? t('crm.admin.users.status_verified')
                        : t('crm.admin.users.status_unverified')
                    }
                  />
                ),
              },
              {
                key: 'blocked',
                label: t('crm.admin.users.col_blocked'),
                children: (
                  <Badge
                    status={detail.user.isBlocked ? 'error' : 'default'}
                    text={
                      detail.user.isBlocked
                        ? t('crm.admin.users.status_blocked')
                        : t('crm.admin.users.status_active')
                    }
                  />
                ),
              },
              {
                key: 'role',
                label: t('crm.admin.users.col_platform_role'),
                children:
                  detail.user.platformRole === 'admin' ? (
                    <EntityStatusChip tone="warning">
                      {t('crm.admin.users.role_admin')}
                    </EntityStatusChip>
                  ) : (
                    <Tag>{t('crm.admin.users.role_user')}</Tag>
                  ),
              },
            ]}
          />

          <section>
            <Text className="mb-2 block text-[13px] font-semibold text-[var(--ant-color-text,#44403c)]">
              {t('crm.admin.users.detail_memberships')} ({detail.memberships.length})
            </Text>
            {detail.memberships.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('crm.admin.users.detail_no_memberships')}
              />
            ) : (
              <div className="flex flex-col gap-1.5">
                {detail.memberships.map((membership) => (
                  <div
                    key={membership.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-[var(--ant-color-border,#e7e5e4)] px-3 py-2"
                  >
                    <div className="min-w-0">
                      <Text className="block truncate text-[13px] font-medium text-[var(--ant-color-text,#44403c)]">
                        {membership.workspace.name}
                      </Text>
                      <Text className="block truncate text-xs text-[var(--ant-color-text-tertiary)]">
                        {membership.workspace.slug} · {membership.role}
                      </Text>
                    </div>
                    <EntityStatusChip tone={membership.status === 'active' ? 'success' : 'default'}>
                      {membership.status}
                    </EntityStatusChip>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <Text className="mb-2 block text-[13px] font-semibold text-[var(--ant-color-text,#44403c)]">
              {t('crm.admin.users.detail_sessions')} ({detail.sessions.length})
            </Text>
            {detail.sessions.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('crm.admin.users.detail_no_sessions')}
              />
            ) : (
              <Table<AdminUserSession>
                showSorterTooltip={TABLE_SORTER_TOOLTIP}
                size="small"
                rowKey="id"
                columns={sessionColumns(t)}
                dataSource={detail.sessions}
                pagination={false}
                scroll={{ x: 480 }}
              />
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}
